/**
 * Presentación de las herramientas en el loader del chat.
 *
 * ## 🔴 Lo que cambió en #1003 F7, y por qué este módulo ya no decide el texto
 *
 * Antes, este fichero era un diccionario `nombre de tool → etiqueta` mantenido a mano, y su
 * fallback componía **`Ejecutando ${toolName}`**. Como el nombre que llega es el del nodo de N8N
 * saneado, el usuario leía `Ejecutando T__Consultar_Ficheros`, `Ejecutando T_DB_BORDAS_FICHAS`
 * (el nombre de un cliente) o `Ejecutando create_role` en su primer minuto de vida en la app.
 * Medido en #956: **44 de 47** nombres caían al fallback y **16 de las 19** entradas del
 * diccionario no correspondían a ningún nodo de ningún workflow.
 *
 * Un diccionario en el frontend no puede saber cómo se llama una herramienta: el acoplamiento vive
 * entre dos repos y una instancia de N8N. Desde F7 **la etiqueta la resuelve el backend**
 * (`server/services/toolCatalogService.js`) desde la tabla `tool` y
 * `server/config/systemToolLabels.json`, y **viaja en el evento** `stream-tool-call-start` como
 * `tool.label` — igual que en el snapshot de reconexión, para que reconectar no cambie el texto.
 *
 * Aquí queda **solo presentación**: el icono, la composición de varias tools y el sub-label de
 * argumentos.
 *
 * ## La regla que no se puede romper
 *
 * 🔴 **Sin etiqueta NO se pinta el identificador.** Se pinta un genérico. El identificador crudo
 * es un detalle interno y, en un caso real, el nombre de un cliente. Si ves el genérico en
 * producción, el fallo está en el catálogo del backend — no se arregla aquí.
 *
 * ⚠️ La cobertura pretende ser del 100 %: toda clave conocida tiene etiqueta, declarada a mano en
 * `systemToolLabels.json` cuando no hay fila de catálogo. El genérico es la red de seguridad, no
 * el plan.
 *
 * ## Hueco declarado: i18n
 *
 * Las etiquetas del catálogo son `tool.name`, que está en castellano en la BD, así que no se
 * pueden traducir sin traducir un dato de negocio. Lo único localizable hoy es el genérico. Un
 * usuario en inglés verá los nombres de herramienta en castellano; era así antes de F7 y sigue
 * siéndolo.
 */

const MAX_ARG_VALUE_LEN = 60;

/**
 * Icono por clave de herramienta. **Presentación pura** — el texto lo manda el backend.
 *
 * Las claves son los nombres de nodo **saneados** (`nodeNameToToolName`), que es como llegan en
 * `toolUse.name`. ⚠️ El saneado **no colapsa los underscores previos**: es `T__RAG`, no `T_RAG` —
 * el catálogo anterior tenía `T_RAG` y por eso nunca casó con nada.
 *
 * Medido contra los 16 motores publicados de PROD el 2026-09-08. Para re-medir:
 * `node scripts/tool-identity-check.js --prod --cross`. Una clave que sobra aquí es inofensiva
 * (nunca casa); una que falta solo cuesta un icono genérico, nunca el texto.
 */
export const TOOL_ICONS = {
  // Conocimiento y búsqueda
  T__RAG: '🧠',
  T__Web_Search: '🌐',
  web_search: '🌐',
  T__Web_Fetch: '📄',
  'T__Wikipedia-Search': '📖',
  T_RAG_IPRESAS_Standalone: '🏗️',
  T_Rag_Bordas: '⚖️',
  T_DB_BORDAS_FICHAS: '🗄️',
  // Ficheros y documentos
  T__Consultar_Ficheros: '📎',
  Consultar_Ficheros: '📎',
  query_files: '📎',
  T__List_Thread_Files: '📁',
  T__Documentos_Word: '📄',
  T__Documentos_PDF: '📕',
  // Renombrada en la ola T2 de #1003 (2026-09-09): el nodo se llamaba `Mermaid Diagram` y saneaba
  // a `Mermaid_Diagram`. Si se deja la clave vieja, el icono deja de aplicarse en silencio.
  mermaid_diagram: '📊',
  // Productividad
  T__Email_Suite: '✉️',
  T__Create_calendar_event: '📅',
  T__Get_calendar_events: '📅',
  T__Microsoft_ToDo: '✅',
  T_Hubspot: '🧲',
  // Piloto de la HubSpot Agent CLI (#1502): el mismo icono que la herramienta actual.
  T__HubspotCLI: '🧲',
  // Cómputo
  T__Execute_Python: '🐍',
  T__Calculator: '🧮',
  Crypto: '🔑',
  // Memoria y contexto
  T__Memoria_Estructurada: '🗂️',
  T__Informacion_Usuario: '👤',
  get_context: '🧭',
  // Agentes y planes
  GetRoles: '👥',
  search_roles: '🔎',
  create_role: '✨',
  update_role: '🛠️',
  delete_role: '🗑️',
  Orchestrator: '▶️',
  Save_Plan_Planner: '💾',
  Get_Plans_for_User_s_Roles: '📋',
  Get_Roles_Tools_Objectives: '🎯',
  // Onboarding
  set_action_cards: '🎴',
  upsert_action_card: '🎴',
  update_onboarding_state: '📌',
  complete_onboarding: '🏁',
  // Protocolo del motor
  Message: '💬',
  Update_Status: '🔄',
  update_status: '🔄',
};

const GENERIC_ICON = '⚙️';

/** Lo que se dice cuando el backend no pudo etiquetar la herramienta. Nunca el identificador. */
const GENERIC_LABEL = 'Usando una herramienta';

function truncate(value, max = MAX_ARG_VALUE_LEN) {
  const str = String(value);
  return str.length > max ? `${str.slice(0, max - 1)}…` : str;
}

function stripTrailingDots(label) {
  return String(label).replace(/[.…]+\s*$/u, '');
}

/**
 * Icono + etiqueta de una herramienta.
 *
 * @param {string} toolName clave saneada, tal como llega del motor
 * @param {(key: string, fallback: string) => string} [t] traductor de react-i18next
 * @param {string | null} [label] la etiqueta que vino en el evento (`tool.label`)
 * @returns {{ icon: string, label: string, isGeneric: boolean }}
 */
export function getToolLabel(toolName, t, label) {
  const icon = (toolName && TOOL_ICONS[toolName]) || GENERIC_ICON;

  if (typeof label === 'string' && label.trim()) {
    return { icon, label: label.trim(), isGeneric: false };
  }

  // 🔴 Sin etiqueta del backend, un genérico. NUNCA `toolName`: ver la cabecera.
  return {
    icon,
    label: t ? t('tools.generic_running', GENERIC_LABEL) : GENERIC_LABEL,
    isGeneric: true,
  };
}

/**
 * Etiqueta compuesta para varias tool-calls en paralelo.
 * 1 tool → su etiqueta · 2 → "A y B" · N≥3 → "A, B y C".
 *
 * Se quitan los puntos finales de cada parte para que la unión lea natural, y se **deduplica por
 * etiqueta**: un agente puede invocar la MISMA herramienta varias veces en un turno (medido en
 * prod: `set_action_cards` ×2 en un turno del Builder), y dos tools distintas pueden compartir
 * rótulo — «Actualizando estado y Actualizando estado» no debe ocurrir por ninguna de las dos vías.
 *
 * @param {Array<{ name: string, label?: string|null }>} tools
 * @param {Function} [t]
 * @returns {string}
 */
export function formatParallelToolsLabel(tools, t) {
  if (!Array.isArray(tools) || tools.length === 0) return '';
  const parts = [...new Set(
    tools.map((tool) => stripTrailingDots(getToolLabel(tool?.name, t, tool?.label).label))
  )];
  if (parts.length === 1) return parts[0];
  const head = parts.slice(0, -1).join(', ');
  const tail = parts[parts.length - 1];
  return `${head} y ${tail}`;
}

/**
 * Sub-label a partir de los argumentos, cuando se reconoce el campo.
 *
 * ⚠️ **Las claves de este `switch` se corrigieron en F7.** Estaban escritas con nombres que no
 * existían en ningún motor (`Search_Email`, `Web_Search`, `T_RAG`…), así que esta función devolvía
 * `null` para todo. Las de ahora son las claves saneadas reales.
 *
 * @param {string} toolName
 * @param {object} args
 * @returns {string | null}
 */
export function formatToolArgsLabel(toolName, args) {
  if (!args || typeof args !== 'object') return null;

  switch (toolName) {
    case 'T__Web_Fetch': {
      // #1546: abre UNA página. Se nombra la web y no la URL entera: la query puede llevar
      // datos personales y al usuario no le aporta nada.
      if (!args.url) return null;
      try {
        return `Abriendo ${new URL(String(args.url)).hostname}`;
      } catch (_) {
        return `Abriendo "${truncate(args.url)}"`;
      }
    }
    case 'T__Web_Search':
    case 'web_search':
    case 'T__Wikipedia-Search': {
      const q = args.query || args.q || args.url;
      return q ? `Buscando "${truncate(q)}"` : null;
    }
    case 'T__RAG':
    case 'T_Rag_Bordas':
    case 'T_RAG_IPRESAS_Standalone':
    case 'T_DB_BORDAS_FICHAS': {
      const q = args.query || args.question;
      return q ? `Consulta: "${truncate(q)}"` : null;
    }
    case 'T__Email_Suite': {
      const subject = args.subject;
      return subject ? `Asunto: "${truncate(subject)}"` : null;
    }
    case 'T__Consultar_Ficheros':
    case 'Consultar_Ficheros':
    case 'query_files': {
      const name = args.fileName || args.file_name || args.query;
      return name ? `Fichero: "${truncate(name)}"` : null;
    }
    case 'search_roles': {
      const q = args.query || args.name;
      return q ? `Buscando "${truncate(q)}"` : null;
    }
    default:
      return null;
  }
}
